import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Paperclip, Download, AlertTriangle } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer, Field, formatDateTime } from "@/components/admin/ui";
import { TICKET_STATUS_META, TICKET_STATUS_ORDER, TICKET_PRIORITY_META } from "./supportMeta";

const selectClass = "rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";

export const TicketDetailDrawer = ({ API, ticketId, meta, canManage, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const load = useCallback(() => {
    axios.get(`${API}/org/support/tickets/${ticketId}`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, ticketId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); load(); onChanged?.(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setStatus = (status) => act(() => axios.patch(`${API}/org/support/tickets/${ticketId}/status`, { status }, { withCredentials: true }));
  const setPriority = (priority) => act(() => axios.patch(`${API}/org/support/tickets/${ticketId}`, { priority }, { withCredentials: true }));
  const setCategory = (category) => act(() => axios.patch(`${API}/org/support/tickets/${ticketId}`, { category }, { withCredentials: true }));
  const assign = (field, value) => act(() => axios.patch(`${API}/org/support/tickets/${ticketId}/assign`, { assigned_team_id: field === "team" ? value || null : data.ticket.assigned_team_id, assigned_user_id: field === "user" ? value || null : data.ticket.assigned_user_id }, { withCredentials: true }));
  const toggleEscalate = () => act(() => axios.patch(`${API}/org/support/tickets/${ticketId}/escalate`, { escalated: !data.ticket.escalated }, { withCredentials: true }));
  const addNote = () => {
    if (!note.trim()) return;
    act(() => axios.post(`${API}/org/support/tickets/${ticketId}/notes`, { text: note.trim() }, { withCredentials: true }).then(() => setNote("")));
  };
  const uploadFile = () => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    act(() => axios.post(`${API}/org/support/tickets/${ticketId}/attachments`, form, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }).then(() => { setFile(null); setFileInputKey((k) => k + 1); }));
  };

  if (!data && !error) return <DetailDrawer open onClose={onClose} title="Loading…" testId="support-ticket-detail-drawer"><p className="text-sm text-slate-400">Loading…</p></DetailDrawer>;
  if (error && !data) return <DetailDrawer open onClose={onClose} title="Ticket" testId="support-ticket-detail-drawer"><p className="text-sm font-semibold text-red-600" data-testid="support-ticket-detail-error">{error}</p></DetailDrawer>;

  const t = data.ticket;

  return (
    <DetailDrawer open onClose={onClose} title={t.display_id} subtitle={t.subject} testId="support-ticket-detail-drawer" wide>
      <div className="space-y-5" data-testid="support-ticket-detail">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="support-ticket-detail-action-error">{error}</p>}

        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full border px-3 py-1 text-xs font-bold ${TICKET_STATUS_META[t.status].cls}`} data-testid="support-ticket-detail-status-badge">{TICKET_STATUS_META[t.status].label}</span>
          <span className={`rounded-full border px-3 py-1 text-xs font-bold ${TICKET_PRIORITY_META[t.priority].cls}`}>{TICKET_PRIORITY_META[t.priority].label}</span>
          {t.escalated && <span className="flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 text-xs font-bold text-red-600 dark:border-red-500/30 dark:text-red-400" data-testid="support-ticket-detail-escalated-badge"><AlertTriangle size={11} /> Escalated</span>}
          {t.reopened_count > 0 && <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-400 dark:border-white/15">Reopened ×{t.reopened_count}</span>}
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center gap-2" data-testid="support-ticket-detail-actions">
            {TICKET_STATUS_ORDER.filter((s) => s !== t.status).map((s) => (
              <button key={s} type="button" disabled={busy} onClick={() => setStatus(s)} data-testid={`support-ticket-action-status-${s}`} className={outlineBtn}>
                {t.status === "resolved" && s !== "resolved" ? `Reopen → ${TICKET_STATUS_META[s].label}` : `Move to ${TICKET_STATUS_META[s].label}`}
              </button>
            ))}
            <button type="button" disabled={busy} onClick={toggleEscalate} data-testid="support-ticket-action-escalate" className={t.escalated ? outlineBtn : `${outlineBtn} border-red-200 text-red-600 dark:border-red-500/30 dark:text-red-400`}>
              {t.escalated ? "Clear Escalation" : "Escalate"}
            </button>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer" value={t.customer_name} testId="support-ticket-detail-customer" />
          <Field label="Created" value={formatDateTime(t.created_at)} testId="support-ticket-detail-created" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Category</p>
            <select disabled={!canManage} value={t.category} onChange={(e) => setCategory(e.target.value)} data-testid="support-ticket-detail-category-select" className={`mt-1 w-full ${selectClass}`}>
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Priority</p>
            <select disabled={!canManage} value={t.priority} onChange={(e) => setPriority(e.target.value)} data-testid="support-ticket-detail-priority-select" className={`mt-1 w-full ${selectClass}`}>
              {meta.priorities.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Team</p>
            <select disabled={!canManage} value={t.assigned_team_id || ""} onChange={(e) => assign("team", e.target.value)} data-testid="support-ticket-detail-team-select" className={`mt-1 w-full ${selectClass}`}>
              <option value="">Unassigned queue</option>
              {meta.teams.map((team) => <option key={team.team_id} value={team.team_id}>{team.name}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Assignee</p>
            <select disabled={!canManage} value={t.assigned_user_id || ""} onChange={(e) => assign("user", e.target.value)} data-testid="support-ticket-detail-assignee-select" className={`mt-1 w-full ${selectClass}`}>
              <option value="">Unassigned</option>
              {meta.users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
        </div>

        <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Description</p><p className="mt-1 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{t.description || "—"}</p></div>

        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Attachments</p>
          <div className="flex flex-wrap gap-2" data-testid="support-ticket-attachments">
            {(t.attachments || []).map((a) => (
              <a key={a.file_id} href={`${API}/org/support/tickets/${ticketId}/attachments/${a.file_id}`} target="_blank" rel="noreferrer" data-testid={`support-ticket-attachment-${a.file_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">
                <Paperclip size={12} /> {a.filename} <Download size={12} />
              </a>
            ))}
          </div>
          {canManage && (
            <div className="mt-2 flex items-center gap-2">
              <input key={fileInputKey} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="support-ticket-attachment-input" className="text-xs" />
              <button type="button" onClick={uploadFile} disabled={!file || busy} data-testid="support-ticket-attachment-upload" className={outlineBtn}>Upload</button>
            </div>
          )}
        </div>

        {canManage && (
          <div className="flex items-start gap-2">
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note — staff-only…" data-testid="support-ticket-note-input" className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
            <button type="button" onClick={addNote} disabled={busy || !note.trim()} data-testid="support-ticket-note-add" className={btn}>Add Note</button>
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Activity</p>
          <div className="space-y-3" data-testid="support-ticket-activity">
            {data.activity.map((a) => (
              <div key={a.event_id} className="flex items-start gap-2.5 text-sm">
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${a.is_internal ? "bg-amber-500" : "bg-[#0052FF]"}`} />
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">{a.label}{a.body ? `: ${a.body}` : ""}</p>
                  <p className="text-xs text-slate-400">{a.actor_name || "System"} · {formatDateTime(a.created_at)}{a.is_internal ? " · Internal" : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DetailDrawer>
  );
};
